import { ReactNode } from "react";
import { Avatar, Image } from "antd";
import { Check, Eye } from "lucide-react";

interface Props {
    item: {
        id: number;
        avatar?: string;
        name: string;
        employee?: string;
        employeeId?: number;
        page?: string;
        time: string;
        content: string;
        edited?: boolean;
        checked?: boolean;
        /** Ảnh đính kèm do API trả về (photos) — hiển thị cùng ảnh nhúng trong nội dung */
        images?: string[];
    };
    /** Nút thao tác (sửa/xóa) hiện ở góc phải, chỉ rõ khi rê chuột vào dòng */
    actions?: ReactNode;
    /** Có nội dung -> thay chỗ hiển thị bằng khung sửa */
    children?: ReactNode;
}

export default function CommentItem({ item, actions, children }: Props) {
    // Tách ảnh ra khỏi nội dung HTML để hiển thị dạng lưới thumbnail có preview
    const htmlImgSrcs: string[] = [];
    const html = (item.content || "")
        .replace(/<img[^>]*src=["']([^"']+)["'][^>]*\/?>/gi, (_m, src) => {
            htmlImgSrcs.push(src);
            return "";
        })
        .replace(/<p>(\s|&nbsp;)*<\/p>/gi, "");

    // Ảnh nhúng trong nội dung + ảnh từ API (photos); bỏ trùng khi cùng một URL
    const imgSrcs = Array.from(new Set([...htmlImgSrcs, ...(item.images || [])].filter(Boolean)));

    return (
        <div
            className={`
                group
                relative
                flex
                gap-3
                px-3
                py-4
                border-b
                border-gray-200
                dark:border-[#2a2a2a]
                hover:bg-[#f7f8fb]
                dark:hover:bg-[#262626]
                ${item.checked ? "bg-[#f7f8fb] dark:bg-[#202020]" : ""}
            `}
        >
            <Avatar
                src={item.avatar || undefined}
                size={36}
                className="flex-shrink-0"
                style={{ background: "#e6f0ff", color: "#21479b", fontWeight: 700 }}
            >
                {item.name.charAt(0).toUpperCase()}
            </Avatar>

            <div className="flex-1">

                {/* Tên */}

                <div className="font-semibold text-[#21479b] text-[14px]">
                    {item.name}
                </div>

                {/* Time */}

                <div className="text-[12px] text-[#8c8c8c] mt-[2px]">
                    {item.time}
                    {item.edited && <span className="ml-1 italic">(đã sửa)</span>}
                </div>

                {/* Page */}

                {item.page && (
                    <div className="text-[#365fd9] text-[13px]">
                        #{item.page}
                    </div>
                )}

                {/* Content — đang sửa thì nhường chỗ cho khung soạn thảo */}

                {children ? (
                    <div className="mt-2">{children}</div>
                ) : (
                    <div
                        className="
                            mt-1
                            text-[15px]
                            text-black
                            dark:text-white
                            leading-6
                            [&_p]:m-0
                            [&_ul]:m-0
                            [&_ul]:pl-5
                        "
                        dangerouslySetInnerHTML={{
                            __html: html,
                        }}
                    />
                )}

                {/* Hình ảnh đính kèm — click mở ảnh lớn, chuyển qua lại giữa các ảnh.
                    Khi đang sửa thì ảnh nằm trong khung soạn thảo nên không hiện ở đây. */}

                {!children && imgSrcs.length > 0 && (
                    <Image.PreviewGroup>
                        <div className="flex flex-wrap gap-2 mt-2">
                            {imgSrcs.map((src, i) => (
                                <div
                                    key={i}
                                    className="
                                        rounded-lg
                                        overflow-hidden
                                        border
                                        border-gray-200
                                        dark:border-[#2a2a2a]
                                        bg-white
                                        dark:bg-[#1f1f1f]
                                    "
                                >
                                    <Image
                                        src={src}
                                        width={80}
                                        height={80}
                                        className="!object-cover cursor-pointer"
                                        preview={{
                                            mask: <Eye size={16} />,
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    </Image.PreviewGroup>
                )}

            </div>

            {actions && (
                <div
                    className="
                        absolute
                        right-3
                        top-3
                        flex
                        items-center
                        gap-1
                        opacity-0
                        group-hover:opacity-100
                        focus-within:opacity-100
                        transition-opacity
                    "
                >
                    {actions}
                </div>
            )}

            {item.checked && (
                <Check
                    size={22}
                    className="
                        absolute
                        right-5
                        top-1/2
                        -translate-y-1/2
                        text-[#4b5cff]
                    "
                />
            )}
        </div>
    );
}
